/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useState, useContext, useEffect, useRef } from 'react';
import { StatusContext } from './StatusContext';
import { BeContext } from './BeContext';
import {
    beSelOpAll, beSelOpInvert, beSelOpLayer, beSelOpTag, beSelOpProbe,
    tagWorkingSet, sampleWorkingSet, getSetting, setSetting,
    exportWorkingSet, ProbeStatus, WsRpcError,
} from '../data';
import SaveDialog from './SaveDialog';
import { formatBytes } from '../utils/formatBytes';

// ── Design system ─────────────────────────────────────────────────────────────
// Surface hierarchy
const BG_APP       = '#0a0a0a';
const BG_PANEL     = '#111319';
const BG_PANEL_2   = '#16191f';
const BG_INPUT     = '#0d1117';
const BG_ACTIVE    = 'rgba(59, 130, 246, 0.16)';
const BG_CONTROL_HOVER = 'rgba(255,255,255,0.06)';

// Borders
const BORDER_SUBTLE = '1px solid rgba(255,255,255,0.08)';
const BORDER_SOFT   = '1px solid rgba(255,255,255,0.10)';
const BORDER_FOCUS  = '1px solid rgba(59,130,246,0.60)';

// Text
const TEXT_PRIMARY   = 'rgba(255,255,255,0.92)';
const TEXT_SECONDARY = 'rgba(255,255,255,0.70)';
const TEXT_TERTIARY  = 'rgba(255,255,255,0.50)';
const TEXT_MUTED     = 'rgba(255,255,255,0.38)';

// Accent & semantic
const ACCENT_PRIMARY = '#3b82f6';
const ACCENT_DANGER  = '#ef4444';
const ACCENT_SUCCESS = '#10b981';

const MONO = 'ui-monospace, "Cascadia Code", Consolas, monospace';

// Global input and control styles
const INPUT_STYLES = `
  .wst-input {
    transition: all 0.2s ease;
  }
  .wst-input::placeholder {
    color: ${TEXT_MUTED};
  }
  .wst-input:focus {
    border-color: ${ACCENT_PRIMARY} !important;
    outline: none;
  }

  .wst-seg {
    transition: all 0.15s ease;
  }
  .wst-seg:hover:not(.wst-seg-active) {
    background: ${BG_CONTROL_HOVER} !important;
  }
  .wst-seg.wst-seg-active {
    background: ${BG_ACTIVE} !important;
  }

  .wst-checkbox {
    accent-color: ${ACCENT_PRIMARY};
  }
`;

const inputStyle: React.CSSProperties = {
    background: BG_INPUT,
    border: BORDER_SOFT,
    borderRadius: 4,
    color: TEXT_PRIMARY,
    padding: '6px 10px',
    fontSize: 13,
    outline: 'none',
    width: '100%',
};

// ── Shared primitives ──────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

function Btn({ label, onClick, variant = 'secondary', disabled = false }: {
    label: string;
    onClick: () => void;
    variant?: BtnVariant;
    disabled?: boolean;
}) {
    const styles: Record<BtnVariant, React.CSSProperties> = {
        primary: {
            background: ACCENT_PRIMARY,
            color: '#fff',
            border: 'none',
        },
        secondary: {
            background: BG_PANEL_2,
            color: TEXT_PRIMARY,
            border: BORDER_SOFT,
        },
        danger: {
            background: 'rgba(239, 68, 68, 0.12)',
            color: '#fca5a5',
            border: '1px solid rgba(239, 68, 68, 0.30)',
        },
        ghost: {
            background: 'transparent',
            color: TEXT_SECONDARY,
            border: BORDER_SOFT,
        },
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                ...styles[variant],
                borderRadius: 4,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 500,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                lineHeight: '20px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
            }}
        >
            {label}
        </button>
    );
}

// Card container for sections
function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div style={{
            background: BG_PANEL_2,
            border: BORDER_SUBTLE,
            borderRadius: 6,
            padding: 16,
        }}>
            {title && (
                <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: TEXT_TERTIARY,
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: BORDER_SUBTLE,
                }}>
                    {title}
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {children}
            </div>
        </div>
    );
}

function FieldGroup({ label, helper, children }: {
    label?: string;
    helper?: string;
    children: React.ReactNode;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {label && (
                <label style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: TEXT_PRIMARY,
                }}>
                    {label}
                </label>
            )}
            {children}
            {helper && (
                <div style={{
                    fontSize: 12,
                    color: TEXT_MUTED,
                    lineHeight: 1.4,
                }}>
                    {helper}
                </div>
            )}
        </div>
    );
}

// ── Inspector (left sidebar) ───────────────────────────────────────────────────
function WorkingSetInspector({ curSize, opHistory, onRefresh, pulseToken }: {
    curSize: number;
    opHistory: string[];
    onRefresh: () => void;
    pulseToken: number;
}) {
    const statusContext = useContext(StatusContext);
    const beContext = useContext(BeContext);
    const [countPulse, setCountPulse] = useState(false);
    const mountedRef = useRef(false);

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }
        setCountPulse(true);
        const timer = setTimeout(() => setCountPulse(false), 420);
        return () => clearTimeout(timer);
    }, [curSize, pulseToken]);

    async function run(fn: () => Promise<unknown>) {
        try {
            await fn();
            await beContext.selRefresh();
            onRefresh();
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    return (
        <div style={{
            width: 180,
            minWidth: 180,
            background: BG_PANEL,
            borderRight: BORDER_SUBTLE,
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            gap: 20,
            overflow: 'hidden',
        }}>
            {/* Working set count */}
            <div>
                <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase' as const,
                    color: TEXT_TERTIARY,
                    marginBottom: 8,
                }}>
                    Working Set
                </div>
                <div style={{
                    fontFamily: MONO,
                    fontSize: 32,
                    fontWeight: 600,
                    color: ACCENT_SUCCESS,
                    lineHeight: 1.1,
                    marginBottom: 2,
                    transform: countPulse ? 'scale(1.75)' : 'scale(1)',
                    transformOrigin: 'left center',
                    transition: 'transform 220ms ease-out',
                    willChange: 'transform',
                }}>
                    {curSize.toLocaleString()}
                </div>
                <div style={{
                    fontSize: 12,
                    color: TEXT_TERTIARY,
                }}>
                    items
                </div>
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase' as const,
                    color: TEXT_TERTIARY,
                }}>
                    Actions
                </div>
                <Btn label="Fill (all)" variant="secondary" onClick={() => run(() => beSelOpAll(false))} />
                <Btn label="Clear" variant="danger" onClick={() => run(() => beSelOpAll(true))} />
                <Btn label="Invert" variant="ghost" onClick={() => run(() => beSelOpInvert())} />
            </div>

            {/* Op history log */}
            {opHistory.length > 0 && (
                <div style={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}>
                    <div style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase' as const,
                        color: TEXT_TERTIARY,
                        marginBottom: 8,
                    }}>
                        History
                    </div>
                    <div style={{
                        overflowY: 'auto',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                    }}>
                        {[...opHistory].reverse().map((entry, i) => (
                            <div
                                key={i}
                                style={{
                                    fontFamily: MONO,
                                    fontSize: 10,
                                    color: TEXT_MUTED,
                                    padding: 6,
                                    background: BG_PANEL_2,
                                    border: BORDER_SUBTLE,
                                    borderRadius: 3,
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {entry}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Probe filter grid ──────────────────────────────────────────────────────────
const PROBE_TYPES = [
    { value: 'dns', label: 'DNS' },
    { value: 'tcp', label: 'TCP' },
    { value: 'curl', label: 'Curl' },
    { value: 'ai', label: 'AI' },
    { value: 'code', label: 'Code' },
] as const;

type ProbeType = typeof PROBE_TYPES[number]['value'];

const PROBE_STATUSES: { value: ProbeStatus; label: string }[] = [
    { value: ProbeStatus.ALL_PASS, label: 'All' },
    { value: ProbeStatus.ANY_PASS, label: 'Any' },
    { value: ProbeStatus.NONE_PASS, label: 'None' },
    { value: ProbeStatus.NO_PROBES, label: 'No Probe' },
];

function ProbeFilterGrid({ selection, onChange }: {
    selection: Record<ProbeType, ProbeStatus | null>;
    onChange: (next: Record<ProbeType, ProbeStatus | null>) => void;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PROBE_TYPES.map((pt) => (
                <div
                    key={pt.value}
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: BORDER_SUBTLE,
                    }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: TEXT_PRIMARY,
                            minWidth: 45,
                        }}
                    >
                        {pt.label}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                        {PROBE_STATUSES.map((ps) => {
                            const active = selection[pt.value] === ps.value;
                            return (
                                <button
                                    key={ps.value}
                                    type="button"
                                    onClick={() =>
                                        onChange({
                                            ...selection,
                                            [pt.value]: active ? null : ps.value,
                                        })
                                    }
                                    className={active ? 'wst-seg wst-seg-active' : 'wst-seg'}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: 12,
                                        fontWeight: active ? 600 : 400,
                                        borderRadius: 3,
                                        border: active ? BORDER_FOCUS : BORDER_SOFT,
                                        background: active ? BG_ACTIVE : BG_INPUT,
                                        color: active ? ACCENT_PRIMARY : TEXT_SECONDARY,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    {ps.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Tab: Membership ───────────────────────────────────────────────────────────
function MembershipTab({ onOp }: { onOp: (label: string) => void }) {
    const statusContext = useContext(StatusContext);
    const beContext = useContext(BeContext);
    const [probeSelection, setProbeSelection] = useState<Record<ProbeType, ProbeStatus | null>>({
        dns: null,
        tcp: null,
        curl: null,
        ai: null,
        code: null,
    });
    const [layerNumber, setLayerNumber] = useState(0);
    const [tagExpr, setTagExpr] = useState('');

    async function applyProbes(add: boolean) {
        const active = PROBE_TYPES.filter((pt) => probeSelection[pt.value] !== null);
        if (active.length === 0) {
            statusContext.postError('Select at least one probe type filter before applying');
            return;
        }
        try {
            for (const pt of active) {
                await beSelOpProbe(pt.value, probeSelection[pt.value]!, add);
            }
            await beContext.selRefresh();
            const summary = active.map((pt) => `${pt.label}:${probeSelection[pt.value]}`).join(' ');
            onOp(`${add ? '+' : '-'} probe(${summary})`);
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    async function runLayer(add: boolean) {
        try {
            await beSelOpLayer(layerNumber, add);
            await beContext.selRefresh();
            onOp(`${add ? '+' : '-'} layer:${layerNumber}`);
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    async function runTagExpr(add: boolean) {
        if (!tagExpr.trim()) return;
        try {
            await beSelOpTag(tagExpr.trim(), add, null);
            await beContext.selRefresh();
            onOp(`${add ? '+' : '-'} tag:${tagExpr}`);
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    const activeProbeCount = PROBE_TYPES.filter(
        (pt) => probeSelection[pt.value] !== null
    ).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
            <Section title="By Probe Filter">
                <ProbeFilterGrid selection={probeSelection} onChange={setProbeSelection} />
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        paddingTop: 12,
                    }}
                >
                    <Btn
                        label={`Remove${activeProbeCount > 0 ? ` (${activeProbeCount})` : ''}`}
                        variant="danger"
                        disabled={activeProbeCount === 0}
                        onClick={() => applyProbes(false)}
                    />
                    <Btn
                        label={`Add${activeProbeCount > 0 ? ` (${activeProbeCount})` : ''}`}
                        variant="secondary"
                        disabled={activeProbeCount === 0}
                        onClick={() => applyProbes(true)}
                    />
                </div>
            </Section>

            <Section title="By Layer Number">
                <FieldGroup>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            className="wst-input"
                            type="number"
                            min={0}
                            max={100}
                            value={layerNumber}
                            onChange={(e) =>
                                setLayerNumber(
                                    Math.max(0, Math.min(100, parseInt(e.target.value) || 0))
                                )
                            }
                            style={{ ...inputStyle, width: 100 }}
                        />
                        <Btn label="Remove" variant="danger" onClick={() => runLayer(false)} />
                        <Btn label="Add" variant="secondary" onClick={() => runLayer(true)} />
                    </div>
                </FieldGroup>
            </Section>

            <Section title="By Tag Expression">
                <FieldGroup>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            className="wst-input"
                            type="text"
                            placeholder="e.g., $.vendor.name"
                            value={tagExpr}
                            onChange={(e) => setTagExpr(e.target.value)}
                            style={{ ...inputStyle, flex: 1 }}
                        />
                        <Btn label="Remove" variant="danger" onClick={() => runTagExpr(false)} />
                        <Btn label="Add" variant="secondary" onClick={() => runTagExpr(true)} />
                    </div>
                </FieldGroup>
            </Section>
        </div>
    );
}

// ── Tab: Tag Ops ──────────────────────────────────────────────────────────────
function TagOpsTab({ onOp }: { onOp: (label: string) => void }) {
    const statusContext = useContext(StatusContext);
    const beContext = useContext(BeContext);
    const [tagName, setTagName] = useState('');

    async function applyTag(add: boolean) {
        if (!tagName.trim()) return;
        try {
            await tagWorkingSet(tagName.trim(), add);
            await beContext.selRefresh();
            onOp(`${add ? '+' : '-'} tag:"${tagName}"`);
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 620 }}>
            <Section title="Bulk Tag Working Set">
                <FieldGroup helper="Adds or removes a flat tag on every fingerprint in the working set.">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            className="wst-input"
                            type="text"
                            placeholder="tag name"
                            value={tagName}
                            onChange={(e) => setTagName(e.target.value)}
                            style={{ ...inputStyle, flex: 1 }}
                        />
                        <Btn
                            label="Remove"
                            variant="danger"
                            disabled={!tagName.trim()}
                            onClick={() => applyTag(false)}
                        />
                        <Btn
                            label="Add"
                            variant="secondary"
                            disabled={!tagName.trim()}
                            onClick={() => applyTag(true)}
                        />
                    </div>
                </FieldGroup>
            </Section>
        </div>
    );
}

// ── Segmented control ──────────────────────────────────────────────────────────
function SegControl<T extends string>({ options, value, onChange }: {
    options: { value: T; label: string }[];
    value: T;
    onChange: (v: T) => void;
}) {
    return (
        <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
            {options.map((opt, i) => (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={value === opt.value ? 'wst-seg wst-seg-active' : 'wst-seg'}
                    style={{
                        flex: 1,
                        padding: '6px 12px',
                        fontSize: 13,
                        fontWeight: value === opt.value ? 600 : 400,
                        background: value === opt.value ? BG_ACTIVE : BG_INPUT,
                        color: value === opt.value ? ACCENT_PRIMARY : TEXT_SECONDARY,
                        border: value === opt.value ? BORDER_FOCUS : BORDER_SOFT,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        borderRadius: 0,
                        marginLeft: i > 0 ? -1 : 0,
                    }}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

// ── Tab: Sample ────────────────────────────────────────────────────────────────
const SAMPLE_SETTING_KEYS = {
    pattern: '/workingSet/sample/pattern',
    minId: '/workingSet/sample/minId',
    maxId: '/workingSet/sample/maxId',
    neverUpdated: '/workingSet/sample/neverUpdated',
    count: '/workingSet/sample/count',
    method: '/workingSet/sample/method',
    mode: '/workingSet/sample/mode',
} as const;

const SAMPLE_SETTING_DESCRIPTIONS = {
    pattern: 'Working set sample filter pattern',
    minId: 'Working set sample minimum fingerprint ID',
    maxId: 'Working set sample maximum fingerprint ID',
    neverUpdated: 'Working set sample only never-updated fingerprints (true|false)',
    count: 'Working set sample count',
    method: 'Working set sample method (random|oldest)',
    mode: 'Working set sample mode (replace|augment)',
} as const;

function SampleTab({ onOp, onPulse }: { onOp: (label: string) => void; onPulse: () => void }) {
    const statusContext = useContext(StatusContext);
    const beContext = useContext(BeContext);
    const [pattern, setPattern] = useState('');
    const [minId, setMinId] = useState('');
    const [maxId, setMaxId] = useState('');
    const [neverUpdated, setNeverUpdated] = useState(false);
    const [count, setCount] = useState(25);
    const [method, setMethod] = useState<'random' | 'oldest'>('random');
    const [mode, setMode] = useState<'replace' | 'augment'>('replace');

    useEffect(() => {
        let cancelled = false;

        async function loadDefaults() {
            const [
                savedPattern,
                savedMinId,
                savedMaxId,
                savedNeverUpdated,
                savedCount,
                savedMethod,
                savedMode,
            ] = await Promise.all([
                getSetting(SAMPLE_SETTING_KEYS.pattern).catch(() => ''),
                getSetting(SAMPLE_SETTING_KEYS.minId).catch(() => ''),
                getSetting(SAMPLE_SETTING_KEYS.maxId).catch(() => ''),
                getSetting(SAMPLE_SETTING_KEYS.neverUpdated).catch(() => 'false'),
                getSetting(SAMPLE_SETTING_KEYS.count).catch(() => '25'),
                getSetting(SAMPLE_SETTING_KEYS.method).catch(() => 'random'),
                getSetting(SAMPLE_SETTING_KEYS.mode).catch(() => 'replace'),
            ]);
            if (cancelled) return;

            setPattern(savedPattern || '');
            setMinId(savedMinId || '');
            setMaxId(savedMaxId || '');
            setNeverUpdated(savedNeverUpdated === 'true');

            const parsedCount = Number(savedCount);
            setCount(
                Number.isFinite(parsedCount) && parsedCount >= 1 ? Math.floor(parsedCount) : 25
            );

            setMethod(savedMethod === 'oldest' ? 'oldest' : 'random');
            setMode(savedMode === 'augment' ? 'augment' : 'replace');
        }

        loadDefaults().catch((e: any) => {
            statusContext.postError(`Unable to load sample defaults: ${e.message}`);
        });

        return () => {
            cancelled = true;
        };
    }, [statusContext]);

    async function apply() {
        const n = Math.floor(Number(count) || 0);
        if (n < 1) {
            statusContext.postError('Count must be at least 1');
            return;
        }
        try {
            const selected = await sampleWorkingSet({
                count: n,
                method,
                mode,
                pattern: pattern.trim() || null,
                minId: minId.trim() ? Number(minId) : null,
                maxId: maxId.trim() ? Number(maxId) : null,
                neverUpdated: neverUpdated || null,
            });
            await Promise.all([
                setSetting(
                    SAMPLE_SETTING_KEYS.pattern,
                    pattern.trim(),
                    SAMPLE_SETTING_DESCRIPTIONS.pattern
                ),
                setSetting(
                    SAMPLE_SETTING_KEYS.minId,
                    minId.trim(),
                    SAMPLE_SETTING_DESCRIPTIONS.minId
                ),
                setSetting(
                    SAMPLE_SETTING_KEYS.maxId,
                    maxId.trim(),
                    SAMPLE_SETTING_DESCRIPTIONS.maxId
                ),
                setSetting(
                    SAMPLE_SETTING_KEYS.neverUpdated,
                    neverUpdated ? 'true' : 'false',
                    SAMPLE_SETTING_DESCRIPTIONS.neverUpdated
                ),
                setSetting(SAMPLE_SETTING_KEYS.count, String(n), SAMPLE_SETTING_DESCRIPTIONS.count),
                setSetting(SAMPLE_SETTING_KEYS.method, method, SAMPLE_SETTING_DESCRIPTIONS.method),
                setSetting(SAMPLE_SETTING_KEYS.mode, mode, SAMPLE_SETTING_DESCRIPTIONS.mode),
            ]);
            await beContext.selRefresh();
            onPulse();
            onOp(`sample:${method}:${mode} → ${selected.length}`);
        } catch (e: any) {
            statusContext.postError(`Error: ${e.message}`);
        }
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxWidth: 620,
        }}>
            {/* Filters section */}
            <Section title="Filters">
                <FieldGroup label="Vendor / Product">
                    <input
                        className="wst-input"
                        type="text"
                        placeholder="e.g., cisco|fortinet"
                        value={pattern}
                        onChange={(e) => setPattern(e.target.value)}
                        style={inputStyle}
                    />
                </FieldGroup>

                <FieldGroup>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            fontSize: 13,
                        }}
                    >
                        <input
                            type="checkbox"
                            className="wst-checkbox"
                            checked={neverUpdated}
                            onChange={(e) => setNeverUpdated(e.target.checked)}
                            style={{
                                width: 16,
                                height: 16,
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        />
                        <span style={{ color: TEXT_PRIMARY }}>Only never-updated fingerprints</span>
                    </label>
                </FieldGroup>

                <FieldGroup label="Fingerprint ID Range">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            className="wst-input"
                            type="number"
                            placeholder="min"
                            value={minId}
                            onChange={(e) => setMinId(e.target.value)}
                            style={{ ...inputStyle, width: 100 }}
                        />
                        <span style={{ color: TEXT_TERTIARY, fontSize: 12 }}>to</span>
                        <input
                            className="wst-input"
                            type="number"
                            placeholder="max"
                            value={maxId}
                            onChange={(e) => setMaxId(e.target.value)}
                            style={{ ...inputStyle, width: 100 }}
                        />
                    </div>
                </FieldGroup>
            </Section>

            {/* Selection section */}
            <Section title="Selection">
                <FieldGroup label="Sample Size">
                    <input
                        className="wst-input"
                        type="number"
                        min={1}
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                        style={{ ...inputStyle, width: 120 }}
                    />
                </FieldGroup>

                <FieldGroup
                    label="Method"
                    helper="Random picks from the filtered fingerprint pool. Oldest first prioritizes the least-recently-updated fingerprints."
                >
                    <SegControl
                        options={[
                            { value: 'random', label: 'Random' },
                            { value: 'oldest', label: 'Oldest first' },
                        ]}
                        value={method}
                        onChange={setMethod}
                    />
                    {method === 'oldest' && (
                        <p
                            style={{
                                fontSize: 12,
                                color: TEXT_MUTED,
                                lineHeight: 1.4,
                                margin: '4px 0 0 0',
                            }}
                        >
                            Sorts by timestamp ascending; missing dates treated as oldest.
                        </p>
                    )}
                </FieldGroup>

                <FieldGroup
                    label="Mode"
                    helper="Replace clears the current working set before applying the sample. Augment keeps existing members and adds the sampled fingerprints."
                >
                    <SegControl
                        options={[
                            { value: 'replace', label: 'Replace' },
                            { value: 'augment', label: 'Augment' },
                        ]}
                        value={mode}
                        onChange={setMode}
                    />
                </FieldGroup>
            </Section>

            {/* Action section */}
            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    paddingTop: 8,
                }}
            >
                <Btn label="Apply" variant="primary" onClick={apply} />
            </div>
        </div>
    );
}

const FINGERPRINT_EXPORT_FIELDS = [
    'fid',
    'pvkey',
    'layer',
    'product',
    'product_tag',
    'vendor',
    'vendor_tag',
    'title',
    'description',
    'presentation',
    'res_type',
    'res_tags',
    'res_score',
    'res_deco',
] as const;

const MATCH_EXPORT_FIELDS = [
    'id',
    'finger_id',
    'pvkey',
    'ip',
    'reverse_dns',
    'forward_dns',
    'recent_dns',
    'tls_cert_subj',
    'tls_sni',
    'tls_alpn',
] as const;

const DNS_FQDN_EXPORT_FIELDS = [
    'match_id',
    'finger_id',
    'pvkey',
    'source_field',
    'fqdn',
] as const;

type FingerprintExportField = typeof FINGERPRINT_EXPORT_FIELDS[number];
type MatchExportField = typeof MATCH_EXPORT_FIELDS[number];
type DnsFqdnExportField = typeof DNS_FQDN_EXPORT_FIELDS[number];

function buildInitialToggleState<T extends string>(items: readonly T[]): Record<T, boolean> {
    const out = {} as Record<T, boolean>;
    for (const item of items) {
        out[item] = true;
    }
    return out;
}

function ExportTab({ onOp }: { onOp: (label: string) => void }) {
    const statusContext = useContext(StatusContext);
    const [rowType, setRowType] = useState<'fingerprints' | 'matches' | 'dns_fqdns'>('fingerprints');
    const [format, setFormat] = useState<'json' | 'csv'>('json');
    const [fingerFields, setFingerFields] = useState<Record<FingerprintExportField, boolean>>(
        buildInitialToggleState(FINGERPRINT_EXPORT_FIELDS)
    );
    const [matchFields, setMatchFields] = useState<Record<MatchExportField, boolean>>(
        buildInitialToggleState(MATCH_EXPORT_FIELDS)
    );
    const [dnsFqdnFields, setDnsFqdnFields] = useState<Record<DnsFqdnExportField, boolean>>(
        buildInitialToggleState(DNS_FQDN_EXPORT_FIELDS)
    );
    const [includeFingerprintMatches, setIncludeFingerprintMatches] = useState(true);
    const [includeFingerprintCategories, setIncludeFingerprintCategories] = useState(true);
    const [preview, setPreview] = useState<{ rowCount: number; byteSize: number; fieldCount: number; fields?: string[]; sampleRows?: any[] } | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [openSaveDialog, setOpenSaveDialog] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [presetKey, setPresetKey] = useState('custom');

    function selectedKeys<T extends string>(items: readonly T[], state: Record<T, boolean>): string[] {
        return items.filter((item) => state[item]);
    }

    const fingerprintFields = selectedKeys(FINGERPRINT_EXPORT_FIELDS, fingerFields);
    const selectedMatchFields = selectedKeys(MATCH_EXPORT_FIELDS, matchFields);
    const selectedDnsFqdnFields = selectedKeys(DNS_FQDN_EXPORT_FIELDS, dnsFqdnFields);

    function setAllMatchFields(enabled: boolean) {
        const next = {} as Record<MatchExportField, boolean>;
        for (const field of MATCH_EXPORT_FIELDS) {
            next[field] = enabled;
        }
        setMatchFields(next);
    }

    function applyPreset(nextPreset: string) {
        setPresetKey(nextPreset);
        switch (nextPreset) {
        case 'fp_full_json':
            setRowType('fingerprints');
            setFormat('json');
            setFingerFields(buildInitialToggleState(FINGERPRINT_EXPORT_FIELDS));
            setIncludeFingerprintMatches(true);
            setIncludeFingerprintCategories(true);
            break;
        case 'match_csv_min': {
            setRowType('matches');
            setFormat('csv');
            const next = buildInitialToggleState(MATCH_EXPORT_FIELDS);
            for (const field of MATCH_EXPORT_FIELDS) {
                next[field] = false;
            }
            next.pvkey = true;
            next.ip = true;
            next.reverse_dns = true;
            next.forward_dns = true;
            next.recent_dns = true;
            next.tls_sni = true;
            setMatchFields(next);
            break;
        }
        case 'match_csv_full':
            setRowType('matches');
            setFormat('csv');
            setAllMatchFields(true);
            break;
        case 'dns_fqdn_csv':
            setRowType('dns_fqdns');
            setFormat('csv');
            const dnsFqdnNext = buildInitialToggleState(DNS_FQDN_EXPORT_FIELDS);
            for (const field of DNS_FQDN_EXPORT_FIELDS) {
                dnsFqdnNext[field] = false;
            }
            dnsFqdnNext.pvkey = true;
            dnsFqdnNext.fqdn = true;
            setDnsFqdnFields(dnsFqdnNext);
            break;
        default:
            break;
        }
    }

    const settings = {
        rowType,
        format,
        fingerprintFields,
        includeFingerprintMatches,
        includeFingerprintCategories,
        matchFields: selectedMatchFields,
        dnsFqdnFields: selectedDnsFqdnFields,
    };

    useEffect(() => {
        let cancelled = false;
        setPreviewLoading(true);
        exportWorkingSet({ settings, action: 'preview' })
            .then((res) => {
                if (cancelled) return;
                setPreview({
                    rowCount: res.rowCount,
                    byteSize: res.byteSize,
                    fieldCount: res.fieldCount,
                    fields: res.fields,
                    sampleRows: res.sampleRows,
                });
            })
            .catch((e: any) => {
                if (cancelled) return;
                statusContext.postError(`Unable to preview export: ${e.message}`);
                setPreview(null);
            })
            .finally(() => {
                if (!cancelled) {
                    setPreviewLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [
        statusContext,
        rowType,
        format,
        includeFingerprintMatches,
        includeFingerprintCategories,
        JSON.stringify(fingerprintFields),
        JSON.stringify(selectedMatchFields),
        JSON.stringify(selectedDnsFqdnFields),
    ]);

    async function runDownload() {
        try {
            setIsDownloading(true);
            const res = await exportWorkingSet({
                settings,
                action: 'download',
                fileName: `working_set_${rowType}`,
            });
            const mime = res.mime || (format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8');
            const content = res.content || '';
            const fileName = res.fileName || `working_set_${rowType}.${format}`;
            const blob = new Blob([content], { type: mime });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
            onOp(`export download ${rowType}:${format} rows=${res.rowCount}`);
            statusContext.postInfo(`Exported and downloaded ${res.rowCount} ${rowType}.`);
        } catch (e: any) {
            if (e instanceof WsRpcError && e.rpcinfo?.code === 'EXPORT_TOO_LARGE') {
                const size = typeof e.rpcinfo?.byteSize === 'number' ? formatBytes(e.rpcinfo.byteSize) : 'unknown size';
                const lim = typeof e.rpcinfo?.limit === 'number' ? formatBytes(e.rpcinfo.limit) : 'unknown limit';
                statusContext.postError(`Export download too large (${size} > ${lim}). Use Export and Save.`);
            } else {
                statusContext.postError(`Export download failed: ${e.message}`);
            }
        } finally {
            setIsDownloading(false);
        }
    }

    const fieldToggleStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        color: TEXT_SECONDARY,
        minWidth: 190,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 860 }}>
            <Section title="What To Export">
                <FieldGroup label="Preset">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select
                            className="wst-input"
                            value={presetKey}
                            onChange={(e) => applyPreset(e.target.value)}
                            style={{ ...inputStyle, maxWidth: 320 }}
                        >
                            <option value="custom">Custom</option>
                            <option value="fp_full_json">Full Fingerprint JSON</option>
                            <option value="match_csv_min">Match CSV Minimal</option>
                            <option value="match_csv_full">Match CSV Full</option>
                            <option value="dns_fqdn_csv">DNS FQDN CSV</option>
                        </select>
                    </div>
                </FieldGroup>

                <FieldGroup label="Row Type">
                    <SegControl
                        options={[
                            { value: 'fingerprints', label: 'Fingerprints' },
                            { value: 'matches', label: 'Matches' },
                            { value: 'dns_fqdns', label: 'DNS FQDNs' },
                        ]}
                        value={rowType}
                        onChange={setRowType}
                    />
                </FieldGroup>

                {rowType === 'fingerprints' ? (
                    <>
                        <FieldGroup label="Fingerprint Fields" helper="Choose columns to include in each fingerprint row.">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {FINGERPRINT_EXPORT_FIELDS.map((field) => (
                                    <label key={field} style={fieldToggleStyle}>
                                        <input
                                            type="checkbox"
                                            className="wst-checkbox"
                                            checked={fingerFields[field]}
                                            onChange={(e) => setFingerFields({ ...fingerFields, [field]: e.target.checked })}
                                        />
                                        <span style={{ fontFamily: MONO }}>{field}</span>
                                    </label>
                                ))}
                            </div>
                        </FieldGroup>

                        <FieldGroup label="Nested Arrays">
                            <label style={fieldToggleStyle}>
                                <input
                                    type="checkbox"
                                    className="wst-checkbox"
                                    checked={includeFingerprintCategories}
                                    onChange={(e) => setIncludeFingerprintCategories(e.target.checked)}
                                />
                                <span>Include categories array</span>
                            </label>
                            <label style={fieldToggleStyle}>
                                <input
                                    type="checkbox"
                                    className="wst-checkbox"
                                    checked={includeFingerprintMatches}
                                    onChange={(e) => setIncludeFingerprintMatches(e.target.checked)}
                                />
                                <span>Include matches array</span>
                            </label>
                        </FieldGroup>
                    </>
                ) : rowType === 'matches' ? (
                    <FieldGroup
                        label="Match Fields"
                        helper="Include pvkey to trace each match row back to a fingerprint."
                    >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {MATCH_EXPORT_FIELDS.map((field) => (
                                <label key={field} style={fieldToggleStyle}>
                                    <input
                                        type="checkbox"
                                        className="wst-checkbox"
                                        checked={matchFields[field]}
                                        onChange={(e) => setMatchFields({ ...matchFields, [field]: e.target.checked })}
                                    />
                                    <span style={{ fontFamily: MONO }}>{field}</span>
                                </label>
                            ))}
                        </div>
                    </FieldGroup>
                ) : (
                    <FieldGroup
                        label="DNS FQDN Fields"
                        helper="Outputs one row per non-empty DNS/FQDN source field per match (reverse_dns, forward_dns, recent_dns, tls_sni)."
                    >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {DNS_FQDN_EXPORT_FIELDS.map((field) => (
                                <label key={field} style={fieldToggleStyle}>
                                    <input
                                        type="checkbox"
                                        className="wst-checkbox"
                                        checked={dnsFqdnFields[field]}
                                        onChange={(e) => setDnsFqdnFields({ ...dnsFqdnFields, [field]: e.target.checked })}
                                    />
                                    <span style={{ fontFamily: MONO }}>{field}</span>
                                </label>
                            ))}
                        </div>
                    </FieldGroup>
                )}
            </Section>

            <Section title="Export Settings">
                <FieldGroup label="Format">
                    <SegControl
                        options={[
                            { value: 'json', label: 'JSON' },
                            { value: 'csv', label: 'CSV' },
                        ]}
                        value={format}
                        onChange={setFormat}
                    />
                </FieldGroup>
            </Section>

            <Section title="Operation Details">
                <div style={{ fontSize: 12, color: TEXT_SECONDARY, lineHeight: 1.5, fontFamily: MONO }}>
                    {previewLoading ? (
                        <div>Calculating export preview...</div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                {[
                                    { label: 'rows', value: String(preview?.rowCount ?? 0) },
                                    { label: 'row type', value: rowType },
                                    { label: 'export type', value: format },
                                    { label: 'fields', value: String(preview?.fieldCount ?? 0) },
                                    { label: 'size', value: formatBytes(preview?.byteSize ?? 0) },
                                ].map((item) => (
                                    <div
                                        key={item.label}
                                        style={{
                                            border: BORDER_SOFT,
                                            background: BG_PANEL_2,
                                            borderRadius: 8,
                                            padding: '8px 10px',
                                            minWidth: 110,
                                        }}
                                    >
                                        <div style={{ fontSize: 10, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            {item.label}
                                        </div>
                                        <div style={{ fontSize: 12, color: TEXT_PRIMARY, marginTop: 2 }}>
                                            {item.value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {preview?.sampleRows && preview.sampleRows.length > 0 && (
                                <div style={{ marginTop: 8, border: BORDER_SOFT, borderRadius: 10, background: BG_PANEL_2, padding: 10 }}>
                                    <div style={{ marginBottom: 6, fontWeight: 'bold', color: TEXT_PRIMARY }}>Sample Data ({preview.sampleRows.length} rows)</div>
                                    {format === 'csv' ? (
                                        <div style={{ overflowX: 'auto', marginTop: 8 }}>
                                            <table style={{ fontSize: 11, borderCollapse: 'collapse', minWidth: '100%' }}>
                                                <thead>
                                                    <tr>
                                                        {(preview.fields || []).map((field: string) => (
                                                            <th
                                                                key={field}
                                                                style={{
                                                                    border: `1px solid ${TEXT_SECONDARY}30`,
                                                                    padding: 4,
                                                                    textAlign: 'left',
                                                                    color: '#aaa',
                                                                    fontWeight: 'bold',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {field}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {preview.sampleRows.map((row: any, idx: number) => (
                                                        <tr key={idx}>
                                                            {(preview.fields || []).map((field: string) => {
                                                                const value = row[field];
                                                                let displayValue = '';
                                                                if (value === null || value === undefined) {
                                                                    displayValue = 'null';
                                                                } else if (typeof value === 'object') {
                                                                    displayValue = `[${Array.isArray(value) ? 'array' : 'object'}]`;
                                                                } else if (typeof value === 'string') {
                                                                    displayValue = value.length > 40 ? value.substring(0, 40) + '...' : value;
                                                                } else {
                                                                    displayValue = String(value);
                                                                }
                                                                return (
                                                                    <td
                                                                        key={field}
                                                                        style={{
                                                                            border: `1px solid ${TEXT_SECONDARY}30`,
                                                                            padding: 4,
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {displayValue}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <details style={{ marginBottom: 8 }}>
                                            <summary style={{ cursor: 'pointer', color: '#aaa', fontSize: 11 }}>
                                                Array [{preview.sampleRows.length} items]
                                            </summary>
                                            <div style={{ marginLeft: 16, marginTop: 6 }}>
                                                {preview.sampleRows.map((row: any, idx: number) => (
                                                    <details key={idx} style={{ marginBottom: 4 }}>
                                                        <summary style={{ cursor: 'pointer', color: '#888', fontSize: 11 }}>
                                                            [{idx}] Object
                                                        </summary>
                                                        <div style={{ marginLeft: 16, marginTop: 4 }}>
                                                            {Object.entries(row).map(([key, value]: [string, any]) => {
                                                                let displayValue = '';
                                                                if (value === null) {
                                                                    displayValue = 'null';
                                                                } else if (typeof value === 'object') {
                                                                    displayValue = Array.isArray(value)
                                                                        ? `Array[${value.length}]`
                                                                        : 'Object';
                                                                } else if (typeof value === 'string') {
                                                                    displayValue = value.length > 50 ? `"${value.substring(0, 50)}..."` : `"${value}"`;
                                                                } else if (typeof value === 'number') {
                                                                    displayValue = String(value);
                                                                } else if (typeof value === 'boolean') {
                                                                    displayValue = value ? 'true' : 'false';
                                                                } else {
                                                                    displayValue = String(value);
                                                                }
                                                                return (
                                                                    <div key={key} style={{ fontSize: 10, marginBottom: 2 }}>
                                                                        <span style={{ color: '#0f0' }}>{key}</span>
                                                                        <span style={{ color: '#888' }}>:</span> {displayValue}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </details>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Section>

            <Section title="Action">
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn
                        label={isDownloading ? 'Downloading...' : 'Export and Download'}
                        variant="primary"
                        onClick={() => { void runDownload(); }}
                        disabled={isDownloading || previewLoading}
                    />
                    <Btn
                        label="Export and Save"
                        variant="secondary"
                        onClick={() => setOpenSaveDialog(true)}
                        disabled={previewLoading}
                    />
                </div>
            </Section>

            <SaveDialog
                isOpen={openSaveDialog}
                title="Export Working Set"
                primaryLabel="Export"
                cloud={true}
                defaultFilename={`working_set_${rowType}.${format}`}
                onCancel={() => setOpenSaveDialog(false)}
                onSave={async (filename: string, cloud: boolean, _metadataOverride, progressId?: string) => {
                    const res = await exportWorkingSet({
                        settings,
                        action: 'save',
                        fileName: filename,
                        cloud,
                        progressId,
                    });
                    onOp(`export save ${rowType}:${format} rows=${res.rowCount}`);
                    statusContext.postInfo(`Export saved: ${res.fileName} (${res.rowCount} rows)`);
                    setOpenSaveDialog(false);
                }}
            />
        </div>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const TABS = ['Sample', 'Tag Ops', 'Membership', 'Export'] as const;
type TabName = typeof TABS[number];

function WorkingSetTools() {
    const beContext = useContext(BeContext);
    const curSize = Object.keys(beContext.beSelection).length;
    const [activeTab, setActiveTab] = useState<TabName>('Sample');
    const [opHistory, setOpHistory] = useState<string[]>([]);
    const [pulseToken, setPulseToken] = useState(0);

    function addOp(label: string) {
        setOpHistory((prev) => [...prev.slice(-19), label]);
    }

    function pulseCounter() {
        setPulseToken((prev) => prev + 1);
    }

    return (
        <div
            style={{
                display: 'flex',
                height: '100%',
                overflow: 'hidden',
                background: BG_APP,
            }}
        >
            <style>{INPUT_STYLES}</style>
            <WorkingSetInspector
                curSize={curSize}
                opHistory={opHistory}
                onRefresh={() => {}}
                pulseToken={pulseToken}
            />

            {/* Main content area */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    background: BG_APP,
                }}
            >
                {/* Tab bar */}
                <div
                    style={{
                        display: 'flex',
                        borderBottom: BORDER_SUBTLE,
                        background: BG_PANEL,
                        flexShrink: 0,
                        paddingLeft: 16,
                    }}
                >
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '12px 20px',
                                fontSize: 13,
                                fontWeight: activeTab === tab ? 600 : 400,
                                background: 'transparent',
                                color:
                                    activeTab === tab
                                        ? ACCENT_PRIMARY
                                        : TEXT_SECONDARY,
                                border: 'none',
                                borderBottom:
                                    activeTab === tab
                                        ? `2px solid ${ACCENT_PRIMARY}`
                                        : '2px solid transparent',
                                cursor: 'pointer',
                                marginBottom: -1,
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 24,
                        background: BG_APP,
                    }}
                >
                    {activeTab === 'Sample' && <SampleTab onOp={addOp} onPulse={pulseCounter} />}
                    {activeTab === 'Tag Ops' && <TagOpsTab onOp={addOp} />}
                    {activeTab === 'Membership' && <MembershipTab onOp={addOp} />}
                    {activeTab === 'Export' && <ExportTab onOp={addOp} />}
                </div>
            </div>
        </div>
    );
}

export default WorkingSetTools;
